import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { toast } from '@/components/ui/use-toast';
import { supabase } from '@/lib/supabase';
import {
  Plus, Save, Loader2, Edit3, Trash2, MapPin, ArrowRight,
  ToggleLeft, ToggleRight, Search, X, AlertCircle, CheckCircle2,
} from 'lucide-react';
import type { AddressAliasRule, AliasMatchType, AliasAppliesTo } from './types';
import { cleanAddress } from './transforms';

// ─── Match type labels ──────────────────────────────────────────────

const MATCH_TYPE_INFO: Record<AliasMatchType, { label: string; color: string }> = {
  exact: { label: 'Exact', color: 'bg-blue-900/40 text-blue-400 border-blue-700/50' },
  starts_with: { label: 'Starts With', color: 'bg-green-900/40 text-green-400 border-green-700/50' },
  contains: { label: 'Contains', color: 'bg-purple-900/40 text-purple-400 border-purple-700/50' },
  regex: { label: 'Regex', color: 'bg-orange-900/40 text-orange-400 border-orange-700/50' },
};

const APPLIES_TO_INFO: Record<AliasAppliesTo, string> = {
  pickup: 'Pickup Only',
  dropoff: 'Dropoff Only',
  both: 'Both',
};

// ─── Props ──────────────────────────────────────────────────────────

interface AddressAliasManagerProps {
  open: boolean;
  onClose: () => void;
  onRulesChanged?: () => void;
}

// ─── Component ──────────────────────────────────────────────────────

const AddressAliasManager: React.FC<AddressAliasManagerProps> = ({ open, onClose, onRulesChanged }) => {
  const [rules, setRules] = useState<AddressAliasRule[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');

  // Edit state
  const [editingRule, setEditingRule] = useState<AddressAliasRule | null>(null);
  const [showEditDialog, setShowEditDialog] = useState(false);

  // New rule form state
  const [formName, setFormName] = useState('');
  const [formMatchType, setFormMatchType] = useState<AliasMatchType>('contains');
  const [formPattern, setFormPattern] = useState('');
  const [formReplacement, setFormReplacement] = useState('');
  const [formAppliesTo, setFormAppliesTo] = useState<AliasAppliesTo>('both');
  const [formNotes, setFormNotes] = useState('');

  // Test input
  const [testInput, setTestInput] = useState('');
  const [testResult, setTestResult] = useState<string | null>(null);

  // ─── Load Rules ─────────────────────────────────────────────────
  const loadRules = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('address_alias_rules')
        .select('*')
        .order('rule_name');
      if (error) throw error;
      setRules((data || []) as AddressAliasRule[]);
    } catch (err: any) {
      console.error('Error loading alias rules:', err);
      toast({ title: 'Error', description: 'Failed to load alias rules', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) loadRules();
  }, [open, loadRules]);

  // ─── Filter ─────────────────────────────────────────────────────
  const filteredRules = rules.filter(r =>
    !search ||
    r.rule_name.toLowerCase().includes(search.toLowerCase()) ||
    r.raw_pattern.toLowerCase().includes(search.toLowerCase()) ||
    r.replacement_value.toLowerCase().includes(search.toLowerCase())
  );

  // ─── Open Edit Dialog ───────────────────────────────────────────
  const openEdit = (rule: AddressAliasRule | null) => {
    if (rule) {
      setEditingRule(rule);
      setFormName(rule.rule_name);
      setFormMatchType(rule.match_type);
      setFormPattern(rule.raw_pattern);
      setFormReplacement(rule.replacement_value);
      setFormAppliesTo(rule.applies_to);
      setFormNotes(rule.notes || '');
    } else {
      setEditingRule(null);
      setFormName('');
      setFormMatchType('contains');
      setFormPattern('');
      setFormReplacement('');
      setFormAppliesTo('both');
      setFormNotes('');
    }
    setShowEditDialog(true);
  };

  // ─── Save Rule ──────────────────────────────────────────────────
  const handleSave = async () => {
    if (!formName.trim() || !formPattern.trim() || !formReplacement.trim()) {
      toast({ title: 'Error', description: 'Name, pattern, and replacement are required', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      if (editingRule) {
        const { error } = await supabase
          .from('address_alias_rules')
          .update({
            rule_name: formName.trim(),
            match_type: formMatchType,
            raw_pattern: formPattern.trim(),
            replacement_value: formReplacement.trim(),
            applies_to: formAppliesTo,
            notes: formNotes.trim() || null,
            updated_at: new Date().toISOString(),
            updated_by: 'admin',
          })
          .eq('id', editingRule.id);
        if (error) throw error;
        toast({ title: 'Updated', description: `Rule "${formName}" updated` });
      } else {
        const { error } = await supabase
          .from('address_alias_rules')
          .insert({
            rule_name: formName.trim(),
            match_type: formMatchType,
            raw_pattern: formPattern.trim(),
            replacement_value: formReplacement.trim(),
            applies_to: formAppliesTo,
            notes: formNotes.trim() || null,
            is_active: true,
            created_by: 'admin',
          });
        if (error) throw error;
        toast({ title: 'Created', description: `Rule "${formName}" created` });
      }
      setShowEditDialog(false);
      await loadRules();
      onRulesChanged?.();
    } catch (err: any) {
      toast({ title: 'Error', description: err.message || 'Failed to save rule', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  // ─── Toggle Active ──────────────────────────────────────────────
  const handleToggleActive = async (rule: AddressAliasRule) => {
    try {
      const { error } = await supabase
        .from('address_alias_rules')
        .update({ is_active: !rule.is_active, updated_at: new Date().toISOString(), updated_by: 'admin' })
        .eq('id', rule.id);
      if (error) throw error;
      setRules(prev => prev.map(r => r.id === rule.id ? { ...r, is_active: !r.is_active } : r));
      onRulesChanged?.();
    } catch (err: any) {
      toast({ title: 'Error', description: err.message || 'Failed to toggle rule', variant: 'destructive' });
    }
  };

  // ─── Delete Rule ────────────────────────────────────────────────
  const handleDelete = async (rule: AddressAliasRule) => {
    if (!confirm(`Delete rule "${rule.rule_name}"?`)) return;
    try {
      const { error } = await supabase
        .from('address_alias_rules')
        .delete()
        .eq('id', rule.id);
      if (error) throw error;
      setRules(prev => prev.filter(r => r.id !== rule.id));
      toast({ title: 'Deleted', description: `Rule "${rule.rule_name}" deleted` });
      onRulesChanged?.();
    } catch (err: any) {
      toast({ title: 'Error', description: err.message || 'Failed to delete rule', variant: 'destructive' });
    }
  };

  // ─── Test Address ───────────────────────────────────────────────
  const handleTest = () => {
    if (!testInput.trim()) {
      setTestResult(null);
      return;
    }
    const result = cleanAddress(testInput, rules, 'pickup');
    setTestResult(result);
  };

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="bg-card border-border max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-foreground flex items-center gap-2">
            <MapPin className="w-5 h-5 text-primary" />
            Address Alias Rules
          </DialogTitle>
          <DialogDescription className="text-muted-foreground text-xs">
            Global rules for cleaning and standardizing addresses in exports. Rules are checked in order — first match wins.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-hidden flex flex-col gap-3">
          {/* Test Panel */}
          <Card className="bg-secondary/30 border-border/50">
            <CardContent className="p-3">
              <Label className="text-[10px] text-muted-foreground uppercase mb-1 block">Test Address Cleaning</Label>
              <div className="flex gap-2 items-start">
                <div className="flex-1">
                  <Input
                    value={testInput}
                    onChange={e => setTestInput(e.target.value)}
                    placeholder="Paste a raw address to test..."
                    className="dark-input text-xs h-8"
                    onKeyDown={e => { if (e.key === 'Enter') handleTest(); }}
                  />
                </div>
                <Button size="sm" onClick={handleTest} className="h-8 text-xs golden-button">
                  Test
                </Button>
              </div>
              {testResult !== null && (
                <div className="mt-2 flex items-center gap-2 text-xs">
                  <span className="text-muted-foreground truncate max-w-[200px]">{testInput}</span>
                  <ArrowRight className="w-3 h-3 text-primary flex-shrink-0" />
                  <span className="text-cyan-400 font-medium">{testResult || '(empty)'}</span>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Search + Add */}
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search rules..."
                className="dark-input text-xs h-8 pl-8"
              />
            </div>
            <Button size="sm" onClick={() => openEdit(null)} className="h-8 text-xs golden-button">
              <Plus className="w-3.5 h-3.5 mr-1" /> Add Rule
            </Button>
          </div>

          {/* Rules List */}
          <div className="flex-1 overflow-y-auto space-y-1 pr-1">
            {loading ? (
              <div className="flex items-center justify-center py-8 text-muted-foreground">
                <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading rules...
              </div>
            ) : filteredRules.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground text-xs">
                <MapPin className="w-8 h-8 mx-auto mb-2 opacity-30" />
                <p>No alias rules found.</p>
              </div>
            ) : (
              filteredRules.map(rule => (
                <div
                  key={rule.id}
                  className={`border rounded-md px-3 py-2 transition-colors ${rule.is_active ? 'border-border/60 hover:border-border' : 'border-border/30 opacity-50'}`}
                >
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={rule.is_active}
                      onCheckedChange={() => handleToggleActive(rule)}
                      className="scale-75 flex-shrink-0"
                    />
                    <span className="text-xs font-medium text-foreground flex-1 truncate">{rule.rule_name}</span>
                    <Badge className={`${MATCH_TYPE_INFO[rule.match_type].color} text-[9px] px-1.5 py-0`}>
                      {MATCH_TYPE_INFO[rule.match_type].label}
                    </Badge>
                    <Badge className="bg-secondary text-muted-foreground text-[9px] px-1.5 py-0">
                      {APPLIES_TO_INFO[rule.applies_to]}
                    </Badge>
                    <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-muted-foreground hover:text-foreground"
                      onClick={() => openEdit(rule)}>
                      <Edit3 className="w-3 h-3" />
                    </Button>
                    <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-red-400 hover:text-red-300"
                      onClick={() => handleDelete(rule)}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                  <div className="flex items-center gap-2 mt-1 text-[10px]">
                    <span className="text-muted-foreground truncate max-w-[250px]">"{rule.raw_pattern}"</span>
                    <ArrowRight className="w-2.5 h-2.5 text-primary flex-shrink-0" />
                    <span className="text-cyan-400 font-medium truncate max-w-[200px]">"{rule.replacement_value}"</span>
                  </div>
                  {rule.notes && (
                    <p className="text-[9px] text-muted-foreground/60 mt-0.5 truncate">{rule.notes}</p>
                  )}
                </div>
              ))
            )}
          </div>

          <div className="text-[10px] text-muted-foreground pt-1 border-t border-border/40">
            {rules.length} total rules | {rules.filter(r => r.is_active).length} active
          </div>
        </div>

        {/* Edit/Create Rule Dialog */}
        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent className="bg-card border-border max-w-md">
            <DialogHeader>
              <DialogTitle className="text-foreground">
                {editingRule ? 'Edit Alias Rule' : 'New Alias Rule'}
              </DialogTitle>
              <DialogDescription className="text-muted-foreground text-xs">
                Define a pattern to match raw addresses and the replacement value.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label className="text-xs text-muted-foreground">Rule Name</Label>
                <Input value={formName} onChange={e => setFormName(e.target.value)} placeholder="e.g. 169 Medical Centre" className="dark-input text-xs" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-muted-foreground">Match Type</Label>
                  <Select value={formMatchType} onValueChange={(v) => setFormMatchType(v as AliasMatchType)}>
                    <SelectTrigger className="dark-input text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="exact">Exact Match</SelectItem>
                      <SelectItem value="starts_with">Starts With</SelectItem>
                      <SelectItem value="contains">Contains</SelectItem>
                      <SelectItem value="regex">Regex</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Applies To</Label>
                  <Select value={formAppliesTo} onValueChange={(v) => setFormAppliesTo(v as AliasAppliesTo)}>
                    <SelectTrigger className="dark-input text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="both">Both</SelectItem>
                      <SelectItem value="pickup">Pickup Only</SelectItem>
                      <SelectItem value="dropoff">Dropoff Only</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Raw Pattern</Label>
                <Input value={formPattern} onChange={e => setFormPattern(e.target.value)} placeholder="Text to match in the raw address..." className="dark-input text-xs" />
                <p className="text-[9px] text-muted-foreground/60 mt-0.5">
                  {formMatchType === 'exact' && 'Must match the entire address exactly (case-insensitive)'}
                  {formMatchType === 'starts_with' && 'Address must start with this text (case-insensitive)'}
                  {formMatchType === 'contains' && 'Address must contain this text anywhere (case-insensitive)'}
                  {formMatchType === 'regex' && 'JavaScript regex pattern (case-insensitive)'}
                </p>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Replacement Value</Label>
                <Input value={formReplacement} onChange={e => setFormReplacement(e.target.value)} placeholder="Cleaned output value..." className="dark-input text-xs" />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Notes (optional)</Label>
                <Textarea value={formNotes} onChange={e => setFormNotes(e.target.value)} placeholder="Why this rule exists..." className="dark-input text-xs min-h-[50px] resize-none" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setShowEditDialog(false)} className="text-muted-foreground">Cancel</Button>
              <Button onClick={handleSave} disabled={saving || !formName.trim() || !formPattern.trim() || !formReplacement.trim()} className="golden-button">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
                {editingRule ? 'Update Rule' : 'Create Rule'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
};

export default AddressAliasManager;
